import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';
import { 
  Building2, CheckCircle, XCircle, RefreshCw, Send, 
  Download, Clock, AlertCircle, Settings, Link2, Unlink,
  FileText, ArrowRight, Loader2, Shield, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QontoIntegrationPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  
  const [status, setStatus] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [showConfig, setShowConfig] = useState(false);
  
  const [config, setConfig] = useState({
    api_key: '',
    organization_id: ''
  });

  useEffect(() => {
    loadStatus();
    loadSyncHistory();
  }, []);

  const getToken = () => localStorage.getItem('token');

  const loadStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/qonto/status`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error('Error loading Qonto status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSyncHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/qonto/sync/history?limit=5`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setSyncHistory(data.history || []);
    } catch (error) {
      console.error('Error loading sync history:', error);
    }
  };

  const handleConfigure = async () => {
    if (!config.api_key) {
      toast({
        title: "Erreur",
        description: "Veuillez entrer votre clé API Qonto",
        variant: "destructive"
      });
      return;
    }

    setConfiguring(true);
    try {
      const response = await fetch(`${API_URL}/api/qonto/configure`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Connecté !",
          description: "Qonto est maintenant connecté comme Plateforme Agréée"
        });
        setShowConfig(false);
        loadStatus();
      } else {
        throw new Error(data.detail || "Erreur de configuration");
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setConfiguring(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await fetch(`${API_URL}/api/qonto/configure`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      toast({
        title: "Déconnecté",
        description: "Qonto a été déconnecté"
      });
      loadStatus();
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/qonto/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Synchronisation lancée",
          description: "Les factures sont en cours de synchronisation avec le PPF"
        });
        setTimeout(loadSyncHistory, 3000);
      } else {
        throw new Error(data.detail || "Erreur de synchronisation");
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleTransmitAll = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/qonto/transmit-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      const data = await response.json();

      toast({
        title: data.success ? "Transmission lancée" : "Info",
        description: data.message
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Intégration Qonto</h1>
          <p className="text-slate-500">Plateforme Agréée pour la facturation électronique</p>
        </div>
        {status?.connected && (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Connecté
          </Badge>
        )}
      </div>

      {/* Architecture Diagram */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-4 text-sm">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
              <FileText className="h-5 w-5 text-blue-600" />
              <span className="font-medium">SmartFacture AI</span>
            </div>
            <ArrowRight className="h-5 w-5 text-slate-400" />
            <div className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-sm">
              <Building2 className="h-5 w-5" />
              <span className="font-medium">Qonto PA</span>
            </div>
            <ArrowRight className="h-5 w-5 text-slate-400" />
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
              <Shield className="h-5 w-5 text-green-600" />
              <span className="font-medium">PPF (État)</span>
            </div>
          </div>
          <p className="text-center text-xs text-slate-500 mt-3">
            Vos factures sont transmises automatiquement au Portail Public de Facturation
          </p>
        </CardContent>
      </Card>

      {/* Status Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Statut Connexion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {status?.connected ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="font-semibold text-green-700">Connecté</span>
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-red-500" />
                  <span className="font-semibold text-red-700">Non connecté</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Envoi e-factures</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {status?.e_invoicing?.sending === 'enabled' ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="font-semibold text-green-700">Activé</span>
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5 text-amber-500" />
                  <span className="font-semibold text-amber-700">
                    {status?.e_invoicing?.sending || 'Non configuré'}
                  </span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Réception e-factures</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {status?.e_invoicing?.receiving === 'enabled' ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="font-semibold text-green-700">Activé</span>
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5 text-amber-500" />
                  <span className="font-semibold text-amber-700">
                    {status?.e_invoicing?.receiving || 'Non configuré'}
                  </span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuration Section */}
      {!status?.connected ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configurer Qonto
            </CardTitle>
            <CardDescription>
              Connectez votre compte Qonto pour activer la transmission automatique vers le PPF
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">Comment obtenir votre clé API ?</h4>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Connectez-vous à votre compte Qonto</li>
                <li>Allez dans Paramètres → Intégrations → API</li>
                <li>Créez une nouvelle clé API avec les permissions "Facturation"</li>
                <li>Copiez la clé et collez-la ci-dessous</li>
              </ol>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="api_key">Clé API Qonto</Label>
                <Input
                  id="api_key"
                  type="password"
                  placeholder="qonto_sk_..."
                  value={config.api_key}
                  onChange={(e) => setConfig({...config, api_key: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="org_id">ID Organisation (optionnel)</Label>
                <Input
                  id="org_id"
                  placeholder="org_..."
                  value={config.organization_id}
                  onChange={(e) => setConfig({...config, organization_id: e.target.value})}
                />
              </div>
            </div>

            <Button onClick={handleConfigure} disabled={configuring} className="w-full">
              {configuring ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4 mr-2" />
              )}
              Connecter Qonto
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleSync} disabled={syncing}>
                  {syncing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Synchroniser
                </Button>
                
                <Button onClick={handleTransmitAll} disabled={syncing} variant="outline">
                  <Send className="h-4 w-4 mr-2" />
                  Transmettre toutes les factures
                </Button>

                <Button variant="destructive" onClick={handleDisconnect}>
                  <Unlink className="h-4 w-4 mr-2" />
                  Déconnecter
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Sync History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Historique des synchronisations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {syncHistory.length > 0 ? (
                <div className="space-y-3">
                  {syncHistory.map((sync, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="text-sm">
                        <span className="text-slate-500">
                          {new Date(sync.synced_at).toLocaleString('fr-FR')}
                        </span>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span className="text-green-600">
                          <Send className="h-3 w-3 inline mr-1" />
                          {sync.sent} envoyées
                        </span>
                        <span className="text-blue-600">
                          <RefreshCw className="h-3 w-3 inline mr-1" />
                          {sync.updated} mises à jour
                        </span>
                        <span className="text-purple-600">
                          <Download className="h-3 w-3 inline mr-1" />
                          {sync.received} reçues
                        </span>
                        {sync.errors > 0 && (
                          <span className="text-red-600">
                            <AlertCircle className="h-3 w-3 inline mr-1" />
                            {sync.errors} erreurs
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">
                  Aucune synchronisation effectuée
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Shield className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">Plateforme Agréée (PA)</h3>
              <p className="text-sm text-white/80">
                Qonto est une Plateforme Agréée par l'État français pour la facturation électronique.
                Toutes vos factures sont transmises de manière sécurisée et conforme au PPF.
                L'archivage légal de 10 ans est inclus.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QontoIntegrationPage;
